import { Component, OnDestroy, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { FormGroup, FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription, distinctUntilChanged, tap } from 'rxjs';
import { CountryCode, getCountryCallingCode, isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { PhoneModel } from '../../../../models';
import { FormField } from '../form';
import { Utils } from '../../../utils';
import { phoneValidator } from '../../../../validators/phone.validator';
import { isPlatformBrowser } from '@angular/common';
import { captureException } from '@sentry/angular';
import { AutocompleteFormFieldComponent } from '../autocomplete-form-field/autocomplete-form-field.component';
import { MatFormField, MatLabel, MatSuffix, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-phone-form-field',
  templateUrl: './phone-form-field.component.html',
  styleUrls: ['./phone-form-field.component.scss'],
  standalone: true,
  imports: [
    AutocompleteFormFieldComponent,
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    ReactiveFormsModule,
    MatIconButton,
    MatSuffix,
    MatIcon,
    MatError,
    TranslocoModule,
  ],
})
export class PhoneFormFieldComponent extends FormField<PhoneModel> implements OnInit, OnDestroy {
  private translocoService = inject(TranslocoService);

  public formGroup = new FormGroup({
    digits: new FormControl<string>(undefined),
    extension: new FormControl<string>(undefined),
  });
  public countryNameOrder = (a, b): number => a.value.name.localeCompare(b.value.name);
  public countries: {[key: string]: {
    value: string,
    viewValue: string,
  }} = {
      FR: {
        value: '+33',
        viewValue: 'France (+33)',
      },
    };
  public getCountryCallingCode = getCountryCallingCode;

  private translationSub: Subscription;

  public isBrowser = false;
  public defaultExtension: string;

  constructor() {
    const platformId = inject(PLATFORM_ID);

    super();
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    this.translationSub = this.translocoService.selectTranslateObject('countries')
      .subscribe((translatedCountries) => {
        const phoneCountries: typeof this.countries = {};
        Object.keys( (translatedCountries))?.forEach( (key) => {
          if (translatedCountries[key]?.code) {
            phoneCountries[key] = {
              value: `+${getCountryCallingCode(translatedCountries[key].code)}`,
              viewValue: `${translatedCountries[key].name} (+${getCountryCallingCode(translatedCountries[key].code)})`,
            };
          } else {
            captureException(`Can not retrieve country calling code. key = ${key} , translatedCountries[key].code = ${translatedCountries[key]?.code} , lang = ${this.translocoService.getActiveLang()}`);
          }
        });
        this.countries = phoneCountries?.FR ? phoneCountries : this.countries;
      });

    if (this.isBrowser) {
      const navigatorCountryCode = navigator?.language?.slice(-2)?.toUpperCase() as CountryCode;
      if (navigatorCountryCode) {
        this.defaultExtension = `+${getCountryCallingCode(navigatorCountryCode)}`;
      }
    }

    this.initForm();
  }

  public updateGroupValue() {
    if (isPlatformBrowser(this.platform)) {
      setTimeout(() => {
        this.formGroup.updateValueAndValidity();
      });
    }
  }

  initForm() {
    this.setValidators();

    // init with default extension or extension already set
    const formModelValue = this.getFormModelValue(this.formModel, this.controlName);
    this.setFormModelValue(this.formModel, `${this.controlName}`, formModelValue?.e164PhoneNumber ? this.parseE164(formModelValue.e164PhoneNumber) : null);

    if (this.form) {
      this.valueSub?.unsubscribe();
      const controlUrl = Utils.stringToURLallowed(this.controlName);
      if (this.form.get(controlUrl)) {
        this.removeControlFromForm();
      }
      try {
        this.form.addControl(controlUrl, this.formGroup, {emitEvent: false});
        this.formGroup.setParent(this.form);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('error init form ', error);
        throw error;
      }
      this.valueSub = this.form.get(controlUrl).valueChanges.pipe(
        distinctUntilChanged(),
        tap((value: {digits: string; extension: string}) => {
          const modelValue = this.getFormModelValue(this.formModel, this.controlName);
          if (value.digits !== modelValue.digits || value.extension !== modelValue.extension) {
            this.setFormModelValue(this.formModel, `${this.controlName}`, value);
          }
          if (this.debug) {
            // eslint-disable-next-line no-console
            console.log(`[${this.controlName}]: value control, value model - formModel`,
              this.formGroup.value,
              this.getFormModelValue(this.formModel, this.controlName),
              this.formModel,
            );
          }
        }),
      ).subscribe();
      if (this.debug) {
        // eslint-disable-next-line no-console
        console.log(
          `[${this.controlName}]:\n form :`,
          this.form,
          '\n control :',
          this.form.get(Utils.stringToURLallowed(this.controlName)),
        );
      }
    }
    if (this.formGroup && this.disabled) {
      this.formGroup.disable();
    }
    this.updateGroupValue();
  }

  protected setFormModel(m: {[key: string]: string | PhoneModel}) {
    const formModelValue = this.getFormModelValue(m, this.controlName);
    // eslint-disable-next-line no-underscore-dangle
    this._formModel = m;
    let parsedModel = new PhoneModel();
    if (typeof formModelValue === 'string') {
      parsedModel = this.parseE164(formModelValue);
    } else if (formModelValue && formModelValue.e164PhoneNumber) {
      parsedModel = this.parseE164(formModelValue.e164PhoneNumber);
    }
    formModelValue.digits = parsedModel.digits;
    formModelValue.extension = parsedModel.extension;
    formModelValue.e164PhoneNumber = parsedModel.e164PhoneNumber;
    this.formGroup.patchValue({
      digits: parsedModel.digits,
      extension: parsedModel.extension,
    } as any, {emitEvent: false});
    if (this.debug) {
      // eslint-disable-next-line no-console
      console.log(`[${this.controlName}]: value control, value model - formModel`,
        this.formGroup.value,
        this.getFormModelValue(this.formModel, this.controlName),
        this.formModel,
      );
    }
    this.updateGroupValue();
  }

  protected setFormModelValue(formModel, controlName: string, value: Partial<PhoneModel>) {
    const controlPath = controlName.split('.');
    if (!formModel) {
      throw new Error('form model is undefined for ' + controlName);
    }
    if (controlPath.length === 1) {
      // case user paste in number with extension code we auto parse it
      if (value && value.digits && value.digits.startsWith('+')) {
        const phoneModel = this.parseE164(value.digits);
        if (phoneModel && phoneModel.e164PhoneNumber?.length > 0) {
          formModel[controlPath[0]] = {
            digits: phoneModel.digits,
            e164PhoneNumber: phoneModel.e164PhoneNumber,
            extension: phoneModel.extension,
          };
          this.setFormModel(formModel);
        }
      } else if (value && value.extension && value.digits) {
        const phoneModel = this.parseE164(`${value.extension}${value.digits}`);
        if (phoneModel && phoneModel.e164PhoneNumber?.length > 0) {
          formModel[controlPath[0]] = {
            digits: phoneModel.digits,
            e164PhoneNumber: phoneModel.e164PhoneNumber,
            extension: phoneModel.extension,
          };
          this.setFormModel(formModel);
        }
      } else if (!value?.digits && !value?.extension) {
        formModel[controlPath[0]].digits = null;
        formModel[controlPath[0]].extension = this.defaultExtension;
        formModel[controlPath[0]].e164PhoneNumber = null;
      } else {
        formModel[controlPath[0]].digits = value.digits;
        formModel[controlPath[0]].extension = value.extension;
        formModel[controlPath[0]].e164PhoneNumber = null;
      }
      this.updateGroupValue();
      return;
    } else {
      return this.setFormModelValue(formModel[controlPath[0]], controlPath.slice(1).join('.'), value);
    }
  }

  protected setValidators() {
    const fcValidators = [
      phoneValidator,
    ];

    // LIST VALIDATORS
    // ---------------
    this.validators?.forEach((validator: string) => {

      if (['required'].includes(validator)) {
        // angular native validators
        fcValidators.push(Validators[validator]);
      }

    });

    // SET VALIDATORS
    // --------------
    if (this.formGroup) {
      this.formGroup.setValidators(fcValidators);
    }
  }


  public displayCountryName(country) {
    return country && country.code ? `${country.name} (+${getCountryCallingCode(country.code)})` : '';
  }

  public cleanField(fieldName: 'extension' | 'digits') {
    this.formGroup.controls[fieldName].setValue(null);
  }

  private parseE164(intPhone: string): PhoneModel {
    if (!intPhone || !isValidPhoneNumber(intPhone)) {
      return {
        digits: '',
        extension: '',
        e164PhoneNumber: '',
      };
    }
    const parsedPhoneNumber = parsePhoneNumber(intPhone);
    return {
      digits: parsedPhoneNumber.formatNational(),
      extension: '+' + parsedPhoneNumber.countryCallingCode,
      e164PhoneNumber: parsedPhoneNumber.number,
    };
  }

  ngOnDestroy(): void {
    this.removeControlFromForm();
    this.translationSub?.unsubscribe();
  }

}
